/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetSearchRow } from '../openapi-types.gen.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetSearch ────────────────────────────────────────────────────────────

export interface useGetSearchArgs { readonly q?: string; readonly type?: string; readonly perTypeLimit?: string; readonly category?: string; readonly status?: string; readonly fields?: readonly ('markets' | 'outcomes' | 'winners' | 'proposals' | 'users' | 'creators' | 'blended')[]; }


async function _useGetSearchQueryFn(
  args: useGetSearchArgs,
  ctx: QueryFunctionContext,
): Promise<GetSearchRow | null> {
  const url = '/v1/search';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  if (args.q !== undefined) params.set('q', String(args.q));
  if (args.type !== undefined) params.set('type', String(args.type));
  if (args.perTypeLimit !== undefined) params.set('perTypeLimit', String(args.perTypeLimit));
  if (args.category !== undefined) params.set('category', String(args.category));
  if (args.status !== undefined) params.set('status', String(args.status));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetSearch',
    routeTemplate: '/v1/search',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetSearchRow;
}


const _useGetSearchFactory = Object.assign(
  (args: useGetSearchArgs) => ({
    queryKey: ['search', 'detail', { q: args.q ?? null, type: args.type ?? null, perTypeLimit: args.perTypeLimit ?? null, category: args.category ?? null, status: args.status ?? null }, args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetSearchQueryFn(args, ctx),
    staleTime: 10000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetSearch',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['search', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetSearchArgs, GetSearchRow>;


export const useGetSearchSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/search',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetSearchFactory },
  parseInput: (raw: unknown): useGetSearchArgs => raw as useGetSearchArgs,
  parseResponse: (raw: unknown): GetSearchRow => raw as GetSearchRow,
  specName: 'useGetSearch' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetSearchRow>;
